import {
  type SelectionEffectFactory,
  type SelectionInputFactory,
} from '@databases/selections';

import { type SelectionFeature } from '../types';

export interface ResolvedFeatures {
  inputs: SelectionInputFactory[];
  effects: SelectionEffectFactory[];
}

/**
 * Partitions features into inputs and effects by collecting
 * each feature's pre-bound factory.
 *
 * Call after `filterSelectionFeatures` (or equivalent) so the list only
 * contains features that passed `isEnabled`.
 *
 * Deduplicates by `key` — if two features share the same key,
 * only the first occurrence is kept. This prevents double-firing
 * when a mode accidentally includes a feature that is also in the
 * always-on list.
 *
 *   features ──► { inputs: [...], effects: [...] }
 */
export function resolveFeatures(features: readonly SelectionFeature[]): ResolvedFeatures {
  const seen = new Set<string>();
  const inputs: SelectionInputFactory[] = [];
  const effects: SelectionEffectFactory[] = [];

  for (const feature of features) {
    if (seen.has(feature.key)) {
      continue;
    }
    seen.add(feature.key);

    if (feature.type === 'input') {
      inputs.push(feature.factory);
    } else {
      effects.push(feature.factory);
    }
  }

  return { inputs, effects };
}
